import React, { useState } from "react"

import {
  showErrorToast,
  showSuccessToast,
} from "@fuse/sharedComponents/themeToast/toastUtils"

import { usePlugins } from "../../../recoil/pluginStudio/hooks"

interface FloatingToolbarUIProps {
  onSave: () => void
  onCreateNew: () => void
  onUpdate: () => void
  eventId?: string | null
  pluginId?: string | null
  isSaving: boolean
  onEditMetadata?: () => void
  isNewPlugin: boolean
}

/**
 * Presentation component for the floating toolbar
 */
export const FloatingToolbarUI: React.FC<FloatingToolbarUIProps> = ({
  onSave,
  onCreateNew,
  onUpdate,
  eventId,
  pluginId,
  isSaving,
  onEditMetadata,
  isNewPlugin,
}) => {
  return (
    <div className="fixed bottom-5 left-1/2 transform -translate-x-1/2 flex space-x-2 p-2 rounded-full bg-gray-800/80 backdrop-blur-sm border border-gray-700 shadow-lg z-50">
      {onEditMetadata && (
        <button
          className="px-5 py-2 rounded-full bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white flex items-center border border-white/10 text-sm"
          onClick={onEditMetadata}
          disabled={isSaving}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 mr-1"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
          </svg>
          <span className="hidden sm:inline">Edit Details</span>
        </button>
      )}

      <button
        className={`px-5 py-2 rounded-full backdrop-blur-sm text-white flex items-center border border-white/10 text-sm
          ${isSaving ? "bg-blue-600/50 cursor-not-allowed" : "bg-white/10 hover:bg-white/20 cursor-pointer"}`}
        onClick={onSave}
        disabled={isSaving}
      >
        {isSaving ? (
          <svg
            className="animate-spin -ml-1 mr-2 h-5 w-5 text-white"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
        ) : (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 mr-1"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
              clipRule="evenodd"
            />
          </svg>
        )}
        <span className="hidden sm:inline">
          {isSaving ? "Saving..." : isNewPlugin ? "Create" : "Update"}
        </span>
      </button>

      {pluginId && !isNewPlugin && (
        <button
          className="px-5 py-2 rounded-full bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white flex items-center border border-white/10 text-sm"
          onClick={() =>
            (window.location.href = `/integrations/${pluginId}${eventId ? `?event_id=${eventId}` : ""}`)
          }
          disabled={isSaving}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 mr-1"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M6.672 1.911a1 1 0 10-1.932.518l.259.966a1 1 0 001.932-.518l-.26-.966zM2.429 4.74a1 1 0 10-.517 1.932l.966.259a1 1 0 00.517-1.932l-.966-.26zm8.814-.569a1 1 0 00-1.415-1.414l-.707.707a1 1 0 101.415 1.415l.707-.708zm-7.071 7.072l.707-.707A1 1 0 003.465 9.12l-.708.707a1 1 0 001.415 1.415zm3.2-5.171a1 1 0 00-1.3 1.3l4 10a1 1 0 001.823.075l1.38-2.759 3.018 3.02a1 1 0 001.414-1.415l-3.019-3.02 2.76-1.379a1 1 0 00-.076-1.822l-10-4z"
              clipRule="evenodd"
            />
          </svg>
          <span className="hidden sm:inline">View Plugin</span>
        </button>
      )}
    </div>
  )
}

/**
 * Container component that connects to Recoil state
 */
const FloatingToolbar: React.FC = () => {
  const { activePlugin, createNewPlugin, saveToServer, updatePlugin } =
    usePlugins()
  const [isSaving, setIsSaving] = useState(false)

  // Check if this is a new plugin (starts with "new-plugin-")
  const isNewPlugin =
    activePlugin?.id?.startsWith("new-plugin-") || activePlugin?.isNew || false

  const handleSave = async () => {
    if (!activePlugin) return

    setIsSaving(true)
    try {
      // Save to server first
      const result = await saveToServer()

      // Only update local state and show success message if the save was successful
      if (result?.success) {
        // Update lastSaved timestamp locally
        updatePlugin(activePlugin.id, {
          lastSaved: Date.now(),
        })

        console.log(
          "[PluginStudio] Plugin saved successfully:",
          activePlugin.id,
        )
        // Show success toast
        showSuccessToast(
          isNewPlugin
            ? "Plugin created successfully!"
            : "Plugin updated successfully!",
        )
      } else {
        console.error(
          "[PluginStudio] Failed to save plugin:",
          result?.error || "Unknown error",
        )
        // Show error toast
        showErrorToast(
          `Failed to ${isNewPlugin ? "create" : "update"} plugin: ${result?.error || "Unknown error"}`,
          { showCloseButton: true },
        )
      }
    } catch (error) {
      console.error("[PluginStudio] Error saving plugin:", error)
      // Show error toast
      showErrorToast(
        `Error ${isNewPlugin ? "creating" : "updating"} plugin: ${error instanceof Error ? error.message : "Unknown error"}`,
        { showCloseButton: true },
      )
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <FloatingToolbarUI
      onSave={handleSave}
      onCreateNew={createNewPlugin}
      onUpdate={handleSave}
      pluginId={activePlugin?.id}
      eventId={null}
      isSaving={isSaving}
      isNewPlugin={isNewPlugin}
    />
  )
}

export default FloatingToolbar
